// video on rtv extra
import React from "react";
import {
  Linking,
  View,
  StyleSheet,
  TouchableOpacity,
  Platform,
} from "react-native";
import { useResponsive } from "../../../../hooks/useResponsive";
import { CarouselItem } from "../../../../types/page";
import RCorner from "../../../RCorner/RCorner";
import {
  DEFAULT_REPLAY_IMAGE,
  DEFAULT_RTV_LIVE_IMAGE,
} from "../../../Image/images/defaultImages";
import RText from "../../../RText";
import variables from "../../../../styles/variables";
import RTouchableOpacity from "../../../RLink/RTouchableOpacity";
import { cleanSlug } from "../../../../utils/cleanSlug";
import { ContentPreviewIcon, VideoPreviewIcon } from "../../../ContentPreview";
import { newsThumbnail } from "../../News/utils";
import { useNavigation } from "@react-navigation/native";
import { StackNavigationProp } from "@react-navigation/stack";
import { handlePressOnDemand, handlePressReplay } from "../../../../utils/nav";
import { useAtom } from "jotai";
import { loadableLiveChannelsAtom } from "../../../../atoms";
import { RootStackParamList } from "../../../../types/route";
import { RTextSize } from "../../../RText/RText";
import { formatRaceTime } from "../../../../utils/TimeCalculator";
import { logEvent } from "../../../../client/analytics/trackPageView";

const { colors, spacing } = variables;
export const Carousel = ({
  carouselItems,
}: {
  carouselItems: CarouselItem[];
}) => {
  const { isMediumAndDown } = useResponsive();
  if (!carouselItems?.length) return null;

  return (
    <View>
      <View
        style={{
          width: "100%",
          backgroundColor: colors.palette.rtv.primary,
          borderRadius: 5,
          flexDirection: isMediumAndDown ? "column" : "row",
        }}
      >
        <SingleCarouselItem carouselItem={carouselItems[0]} />
      </View>
      <RCorner size="large" borderTopRightRadius />
    </View>
  );
};

export const SingleCarouselItem = ({
  carouselItem,
  position,
  loading = "lazy",
  isAttachment = false,
}: {
  carouselItem: CarouselItem;
  position?: number;
  loading?: "lazy" | "eager";
  isAttachment?: boolean;
}) => {
  const {
    isMediumAndDown,
    isXSmallOnly,
    isSmallOnly,
    isMediumOnly,
    isLargeOnly,
    isLargeAndUp,
  } = useResponsive();
  const navigation = useNavigation<StackNavigationProp<RootStackParamList>>();
  const [liveChannels] = useAtom(loadableLiveChannelsAtom);

  if (!carouselItem) return;

  //cms returns action_url with a leading slash, which causes Linking.openURL to fail if if's not an internal link
  //the below removes the leading slash if it's an external link

  let actionUrl = carouselItem?.action_url;
  if (actionUrl && actionUrl.includes("http") && actionUrl[0] === "/")
    actionUrl = actionUrl.slice(1, actionUrl.length);

  const fontSize = (): RTextSize => {
    if (isXSmallOnly) return "sm";
    if (position === 0) {
      if (isSmallOnly) return "md";
      if (isMediumOnly) return "lg";
      if (isLargeAndUp) return "xl";
      return "lg";
    }

    if (isSmallOnly) return "xxxs";
    if (isMediumOnly) return "xxs";
    if (isLargeOnly) return "xs";
    if (isLargeAndUp) return "md";
  };

  const lineHeight = () => {
    if (isXSmallOnly) return 18;
    if (position === 0) {
      if (isSmallOnly) return 18;
      if (isMediumOnly) return 20;
      return 25;
    }
    if (position > 0) {
      if (isMediumAndDown) return 12;
      if (isLargeOnly || isLargeAndUp) return 20;
    }
  };

  const paddingHorizontal = () => {
    if (position === 0) {
      if (isMediumAndDown) return spacing.xsmall;
      if (isLargeOnly || isLargeAndUp) return spacing.small;
    }

    if (position > 0) {
      if (isMediumAndDown) return spacing.xxsmall;
      if (isLargeOnly || isLargeAndUp) return spacing.xsmall;
    }
  };

  const logCarouselClick = () => {
    logEvent({
      key: `${isAttachment ? "widget-" : ""}carousel${position + 1}-clicked`,
      data: {},
    });
  };

  const TextOverlay = ({ title }: { title: string }) => {
    if (!title) return;
    return (
      <View style={[styles.overlay]}>
        <RText
          size={fontSize()}
          numberOfLines={2}
          weight="bold"
          color="white"
          family="secondary"
          style={[
            styles.headline,
            {
              paddingHorizontal: paddingHorizontal(),
            },
          ]}
          lineHeight={lineHeight()}
        >
          {title}
        </RText>
      </View>
    );
  };

  switch (carouselItem.content_type) {
    case "demand_video":
      return (
        <RTouchableOpacity
          shouldOpenInNewTab
          style={styles.container}
          navScreen="IndividualOnDemand"
          navParams={{
            id: carouselItem.demand_video.id,
            slug: cleanSlug(carouselItem.demand_video.categories?.[0].slug),
          }}
          preNavAction={() => logCarouselClick()}
        >
          <VideoPreviewIcon
            thumbnailUrl={
              carouselItem.demand_video.catchup_video?.placeholder_image_url ||
              carouselItem.demand_video.placeholder_image_url ||
              ""
            }
            onPress={() => {
              Platform.OS !== "web" && // needed for native navigation
                handlePressOnDemand(
                  {
                    id: carouselItem.demand_video.id,
                    slug: cleanSlug(
                      carouselItem.demand_video.categories?.[0].slug,
                    ),
                  },
                  navigation,
                );
            }}
            size="large"
            inList
          />
          <TextOverlay
            title={carouselItem?.title || carouselItem.demand_video.title || ""}
          />
        </RTouchableOpacity>
      );
    case "advertisment":
      return (
        <TouchableOpacity
          onPress={() => {
            try {
              Linking.openURL(actionUrl);
            } catch (error) {
              console.error("Failed to open URL:", error);
            }
            logCarouselClick();
          }}
          style={styles.container}
        >
          <ContentPreviewIcon
            thumbnailUrl={
              (carouselItem?.asset?.viewer_sources?.length &&
                carouselItem?.asset?.viewer_sources[0]?.url) ||
              carouselItem?.asset?.placeholder_image_url
            }
            loading={loading}
            inList
          />
          <TextOverlay title={carouselItem.title || ""} />
        </TouchableOpacity>
      );
    case "image":
      return (
        <TouchableOpacity
          onPress={() => {
            try {
              Linking.openURL(actionUrl);
            } catch (error) {
              console.error("Failed to open URL:", error);
            }
            logCarouselClick();
          }}
          style={styles.container}
        >
          <ContentPreviewIcon
            thumbnailUrl={
              (carouselItem?.asset?.viewer_sources?.length &&
                carouselItem?.asset?.viewer_sources[0]?.url) ||
              carouselItem?.asset?.placeholder_image_url
            }
            loading={loading}
            inList
          />
          <TextOverlay title={carouselItem.title || ""} />
        </TouchableOpacity>
      );

    case "news_article":
      return (
        <RTouchableOpacity
          navScreen="NewsPost"
          navParams={{
            slug: cleanSlug(carouselItem.news_article.slug),
          }}
          style={styles.container}
          preNavAction={() => logCarouselClick()}
        >
          <ContentPreviewIcon
            thumbnailUrl={newsThumbnail(carouselItem.news_article)}
            loading={loading}
            inList
          />
          <TextOverlay title={carouselItem?.news_article?.headline || ""} />
        </RTouchableOpacity>
      );
    case "race_replay":
      return (
        <View style={styles.container}>
          <VideoPreviewIcon
            thumbnailUrl={
              carouselItem?.race.replay_video?.placeholder_image_url ||
              carouselItem?.race.replay_videos[0]?.placeholder_image_url ||
              DEFAULT_REPLAY_IMAGE
            }
            onPress={() => {
              handlePressReplay(carouselItem.race, navigation);
              logCarouselClick();
            }}
            size="large"
            inList
          />
          <TextOverlay
            title={
              `${
                formatRaceTime(carouselItem?.race?.start_time_scheduled) || ""
              } - ${carouselItem?.race?.meeting?.track.name || ""} ` || ""
            }
          />
        </View>
      );
    case "live_stream":
      return (
        <View style={styles.container}>
          <VideoPreviewIcon
            isLive
            thumbnailUrl={
              carouselItem?.asset?.placeholder_image_url ||
              (liveChannels.state === "hasData" &&
                liveChannels.data[0]?.backgroundImage) ||
              DEFAULT_RTV_LIVE_IMAGE
            }
            onPress={() => {
              navigation.navigate("Watch");
              logCarouselClick();
            }}
            size="large"
            inList
          />
          <TextOverlay title={carouselItem.title || ""} />
        </View>
      );
    default:
      return <></>;
  }
};

const styles = StyleSheet.create({
  container: {
    width: "100%",
    height: "100%",
  },
  overlay: {
    position: "absolute",
    bottom: 0,
    paddingHorizontal: 5,
    padding: variables.spacing.xsmall,
    borderBottomLeftRadius: 5,
    borderBottomRightRadius: 5,
    backgroundColor: colors.palette.slightlyTransparent.primary,
    width: "100%",
  },
  headline: {
    width: "100%",
    marginTop: "auto",
    marginBottom: "auto",
  },
});
